// Offline-first data layer. Reads fall back to a localStorage cache; writes go
// out immediately when online, otherwise queue in an outbox that flushes on
// reconnect. localStorage is enough for two kids' profiles + modest history,
// and it survives with no signal at a field.
import { api } from "./api";
import type { Profile, SessionRecord } from "../data/schema";

const K = {
  profiles: "training.profiles",
  sessions: (pid: string) => `training.sessions.${pid}`,
  outbox: "training.outbox",
};

type OutboxItem =
  | { kind: "profile"; payload: Profile }
  | { kind: "session"; payload: SessionRecord };

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, val: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* private mode / quota — ignore, network is source of truth */
  }
}

// ---- outbox ---------------------------------------------------------------
function getOutbox(): OutboxItem[] {
  return read<OutboxItem[]>(K.outbox, []);
}
function setOutbox(items: OutboxItem[]): void {
  write(K.outbox, items);
}
function enqueue(item: OutboxItem): void {
  setOutbox([...getOutbox(), item]);
}

export function pendingCount(): number {
  return getOutbox().length;
}

export async function flushOutbox(): Promise<number> {
  if (!navigator.onLine) return 0;
  const items = getOutbox();
  const remaining: OutboxItem[] = [];
  let sent = 0;
  for (const item of items) {
    try {
      if (item.kind === "profile") await api.putProfile(item.payload);
      else await api.postSession(item.payload);
      sent++;
    } catch {
      remaining.push(item); // keep for next flush
    }
  }
  setOutbox(remaining);
  return sent;
}

// ---- profiles -------------------------------------------------------------
export async function loadProfiles(): Promise<Profile[]> {
  try {
    const list = await api.profiles();
    write(K.profiles, list);
    return list;
  } catch {
    return read<Profile[]>(K.profiles, []);
  }
}

export async function saveProfile(p: Profile): Promise<void> {
  const updated = { ...p, updatedAt: new Date().toISOString() };
  const cached = read<Profile[]>(K.profiles, []);
  write(
    K.profiles,
    cached.some((x) => x.id === updated.id)
      ? cached.map((x) => (x.id === updated.id ? updated : x))
      : [...cached, updated]
  );
  try {
    await api.putProfile(updated);
  } catch {
    enqueue({ kind: "profile", payload: updated });
  }
}

// ---- sessions -------------------------------------------------------------
export async function loadSessions(profileId: string): Promise<SessionRecord[]> {
  try {
    const list = await api.sessions(profileId);
    write(K.sessions(profileId), list);
    return list;
  } catch {
    return read<SessionRecord[]>(K.sessions(profileId), []);
  }
}

export function cachedSessions(profileId: string): SessionRecord[] {
  return read<SessionRecord[]>(K.sessions(profileId), []);
}

export async function saveSession(s: SessionRecord): Promise<void> {
  // Write-through to cache first so history + recency work offline immediately.
  const cached = read<SessionRecord[]>(K.sessions(s.profileId), []);
  write(
    K.sessions(s.profileId),
    [s, ...cached.filter((x) => x.id !== s.id)].slice(0, 200)
  );
  try {
    await api.postSession(s);
  } catch {
    enqueue({ kind: "session", payload: s });
  }
}

// Completed-session drill history, newest first — feeds the generator's recency.
export function drillHistory(profileId: string): { drillId: string; date: string }[] {
  const out: { drillId: string; date: string }[] = [];
  for (const s of cachedSessions(profileId)) {
    if (s.status !== "completed") continue;
    for (const pd of s.plan.drills) out.push({ drillId: pd.drill.id, date: s.date });
  }
  return out;
}

// Flush whenever we regain connectivity.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => void flushOutbox());
}
